import { Select } from 'antd';
import { useNotify } from 'react-admin';
import { makeStyles } from '@mui/styles';
import { useListContext } from 'ra-core';
import { useModalContext } from '@providers';
import React, { useEffect, useState } from 'react';

import { useRequest } from '@hooks';
import { adminHttp, creditCallHttp } from '@network';
import { BlueButton, BlueOutlineButton } from '@components';

const propsList = ['Task Admin'];
type PropType = 'Task Admin';
type Option = {value: number | string; label: string};

export const CreditCallListActions = () => {
  const { selectedIds, refetch } = useListContext();
  const [prop, setProp] = useState<PropType>('Task Admin');
  const { hideModal } = useModalContext();
  const notify = useNotify();
  const cls = useStyles();

  const [ownersOptions, setOwnersOptions] = useState<Option[]>([]);
  const [value, setValue] = useState<number | string>('');

  const resetValues = () => {
    setProp('Task Admin');
    setValue('');
  };

  const onCancel = () => {
    resetValues();
    hideModal();
  };

  const { loading, submit } = useRequest(async () => {
    if (!prop) {
      return notify('No property is selected');
    }
    if (!Array.isArray(selectedIds) || selectedIds.length === 0) {
      return notify('No records are selected');
    }
    if (prop === 'Task Admin' && !value) {
      return notify('No Task Admin is selected');
    }

    const updatedTotal = await creditCallHttp.updateBulk({ selectedIds, prop, value });

    notify(`Updated ${updatedTotal} records`);
    onCancel();
    refetch();
  });

  useEffect(() => void resetValues(), [prop]);

  useEffect(() => {
    // load owners
    (async () => {
      const owners = await adminHttp.getTaskAdmins();
      const sortedOwners = owners.map(admin => ({
        value: admin.id,
        label: admin.name,
      })).sort((a, b) => a.label > b.label ? 1 : -1);
      setOwnersOptions(sortedOwners);
    })();
  }, []);

  return (
    <div className={cls.box}>
      <div className={cls.title}>Properties to update</div>
      <Select value={prop} onChange={v => setProp(v)} className={cls.select}
        placeholder="Select a property to edit">
        {propsList.map(propItem => (
          <Select.Option key={propItem} value={propItem}>
            {propItem}
          </Select.Option>
        ))}
      </Select>

      <div className={cls.content}>
        {prop && <div className={cls.title}>{prop}</div>}
        {prop === 'Task Admin' && (
          <Select
            value={value}
            onChange={v => setValue(+v)}
            className={cls.select}
            placeholder="Select Task Admin"
            showSearch
            optionFilterProp="children"
            status={!value ? 'error' : undefined}
          >
            {ownersOptions.map(option => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
        )}
      </div>

      <div className={cls.controls}>
        <BlueButton className={cls.btn} onClick={submit} disabled={loading}>
          Update
        </BlueButton>
        <BlueOutlineButton className={cls.btn} onClick={onCancel}>
          Cancel
        </BlueOutlineButton>
      </div>
    </div>
  );
};

const useStyles = makeStyles({
  box: {},
  group: {
    marginTop: 15,
  },
  title: {
    fontSize: 20,
    lineHeight: 1,
    fontWeight: 500,
  },
  select: {
    marginTop: 10,
    width: '100%',
    borderRadius: 8,
    border: '1px solid var(--background-color)',
    backgroundColor: 'var(--background-color)',
    '& .ant-select-selector': {
      backgroundColor: 'transparent !important',
    },
  },
  content: {
    marginTop: 15,
  },
  controls: {
    marginTop: 20,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  btn: {
    margin: '0 7px',
    padding: '5px 29px !important',
    height: 34,
    borderRadius: 8,
    textTransform: 'none',
  },
});
